import { i18nNsFactory } from '@atlas-frontend/i18n';
import translations from './RelationsLegend.translations.json';
import styled from 'styled-components';
import {
  colorAccent1,
  colorGray,
  colorGreen,
  colorInfo,
  colorLabel,
  colorPink,
  colorPurple,
  colorYellow,
  fontSize,
} from '@atlas-frontend/design-tokens';
import * as Icon from 'react-feather';
import dataConflictsIcon from '../../assets/img/data-conflicts.svg';
import unknownDataConflictsIcon from '../../assets/img/unknown-data-conflicts.svg';
import databaseGreenIcon from '../../assets/img/database-green.svg';
import databaseRedIcon from '../../assets/img/database-red.svg';
import { TextEllipsis, Tooltip } from '@atlas-frontend/sdk';
import { ReactNode, useMemo } from 'react';

interface LegendSubItem {
  icon: ReactNode;
  label: string;
  iconHasPadding?: boolean;
}

interface LegendItem extends LegendSubItem {
  subItems?: LegendSubItem[];
  tooltipTitle?: string;
}

const vrIcon = (color: string) => (
  <RelationsLegendItemIconText color={color}>VR</RelationsLegendItemIconText>
);

const { addTranslations, useTranslation } = i18nNsFactory('relations-legend');
addTranslations(translations);

export const RelationsLegend = () => {
  const { t } = useTranslation();

  const legendItems = useMemo<LegendItem[]>(
    () => [
      {
        icon: <RelationsLegendItemIconText>G</RelationsLegendItemIconText>,
        label: t('label.group'),
        iconHasPadding: true,
      },
      {
        icon: (
          <RelationsLegendItemIconText color={colorPurple[5]}>
            VV
          </RelationsLegendItemIconText>
        ),
        label: t('label.intermediate'),
      },
      {
        icon: <Icon.AlertTriangle size={18} color={colorAccent1} />,
        label: t('label.calculationError'),
      },
      {
        icon: <Icon.AlertTriangle size={18} color={colorInfo} />,
        label: t('label.calculationInfo'),
      },
      {
        icon: <Icon.Loader size={18} color={colorGray[7]} />,
        label: t('label.calculationInProgress'),
      },
      {
        icon: <img src={dataConflictsIcon} alt="data-conflicts-icon" />,
        label: t('label.knownDataConflicts'),
        tooltipTitle: t('label.dataConflicts'),
        iconHasPadding: true,
        subItems: [
          {
            icon: <img src={dataConflictsIcon} alt="data-conflicts-icon" />,
            label: t('label.knownDataConflicts'),
            iconHasPadding: true,
          },
          {
            icon: (
              <img
                src={unknownDataConflictsIcon}
                alt="unknown-data-conflicts-icon"
              />
            ),
            label: t('label.unknownDataConflicts'),
            iconHasPadding: true,
          },
        ],
      },
      {
        icon: <img src={databaseGreenIcon} alt="database-green-icon"></img>,
        label: t('label.inputsLoaded'),
      },
      {
        icon: <Icon.Database size={18} color={colorYellow[5]} />,
        label: t('label.newerData'),
      },
      {
        icon: <img src={databaseRedIcon} alt="database-red-icon"></img>,
        label: t('label.noData'),
      },
      {
        icon: <RelationsLegendItemIconText>VR</RelationsLegendItemIconText>,
        label: t('label.validationStarted'),
        subItems: [
          { icon: vrIcon(colorPink[5]), label: t('label.technical') },
          { icon: vrIcon(colorYellow[5]), label: t('label.warning') },
          { icon: vrIcon(colorInfo), label: t('label.info') },
          { icon: vrIcon(colorGreen[5]), label: t('label.validationSuccess') },
          { icon: vrIcon(colorGray[5]), label: t('label.validationMissing') },
        ],
      },
    ],
    [t],
  );

  return (
    <RelationsLegendContainer>
      {legendItems.map((item, index) => (
        <RelationsLegendItem key={index}>
          <RelationsLegendItemIcon hasPadding={item.iconHasPadding}>
            {item.icon}
          </RelationsLegendItemIcon>
          {item.subItems ? (
            <Tooltip
              title={
                <RelationsLegendTooltipContainer>
                  <strong>{item.tooltipTitle ?? item.label}</strong>
                  {item.subItems.map((sub, i) => (
                    <RelationsLegendItem key={i}>
                      <RelationsLegendItemIcon hasPadding={sub.iconHasPadding}>
                        {sub.icon}
                      </RelationsLegendItemIcon>
                      <RelationsLegendItemLabel>
                        {sub.label}
                      </RelationsLegendItemLabel>
                    </RelationsLegendItem>
                  ))}
                </RelationsLegendTooltipContainer>
              }
            >
              <TextEllipsis tooltip={null}>
                <RelationsLegendItemLabel>
                  {item.label}
                </RelationsLegendItemLabel>
              </TextEllipsis>
            </Tooltip>
          ) : (
            <TextEllipsis tooltip={<TooltipText>{item.label}</TooltipText>}>
              <RelationsLegendItemLabel>{item.label}</RelationsLegendItemLabel>
            </TextEllipsis>
          )}
        </RelationsLegendItem>
      ))}
    </RelationsLegendContainer>
  );
};

const RelationsLegendContainer = styled.div`
  display: grid;
  grid-template-columns: repeat(10, 1fr);
  gap: 16px;
`;

const RelationsLegendItem = styled.span`
  display: grid;
  grid-template-columns: 20px auto;
  align-items: center;
  min-width: 0;
  gap: 10px;
`;

interface RelationsLegendItemIconProps {
  hasPadding?: boolean;
}

export const RelationsLegendItemIcon = styled.span<RelationsLegendItemIconProps>`
  display: flex;
  align-items: center;
  justify-content: center;
  border: 1px solid ${colorGray[5]};
  color: ${colorLabel};
  height: 22px;
  width: 24px;
  border-radius: 50%;
  background-color: white;
  flex-shrink: 0;
  padding: ${({ hasPadding = false }) => (hasPadding ? '6px' : '0')};
`;

const RelationsLegendItemLabel = styled.span`
  font-size: 12px;
`;

interface RelationsLegendItemIconTextProps {
  color?: string;
}
export const RelationsLegendItemIconText = styled.span<RelationsLegendItemIconTextProps>`
  color: ${({ color = colorLabel }) => color};
`;

const RelationsLegendTooltipContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
`;

export const TooltipText = styled.span`
  font-size: ${fontSize};
`;
